import { lazy, Suspense, useCallback, useEffect, useId, useState } from "react";
import type { ReactNode } from "react";
import { downloadUrl, previewUrl } from "../../lib/api/files";
import { Modal } from "../ui/Modal";
import type { FileDTO } from "../../lib/api/types";

// Lazy so pdf.js (~400 KB) only downloads when a PDF preview is actually opened.
const PdfViewer = lazy(() => import("./PdfViewer").then((m) => ({ default: m.PdfViewer })));

function PreviewLoading() {
  return (
    <div className="flex h-[70vh] items-center justify-center text-body text-text-muted">
      Loading preview…
    </div>
  );
}

interface FilePreviewModalProps {
  file: FileDTO | null;
  onClose: () => void;
}

function PreviewError({ href }: { href: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-5 py-16 text-center">
      <p className="text-body text-text-muted">Can&apos;t preview this file. Download it to view.</p>
      <a
        href={href}
        download
        className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-600 px-4 text-body font-medium text-white hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <span aria-hidden="true">↓</span>
        <span>Download</span>
      </a>
    </div>
  );
}

export function FilePreviewModal({ file, onClose }: FilePreviewModalProps) {
  const titleId = useId();
  const [failed, setFailed] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const isImage = file?.mimeType.startsWith("image/") ?? false;
  const isPdf = file?.mimeType === "application/pdf";
  const canPreview = isImage || isPdf;

  const onPreviewError = useCallback(() => setFailed(true), []);

  // Images: fetch into a same-origin blob: URL (sidesteps cookie-in-<img> and
  // framing quirks). PDFs are handled separately by <PdfViewer> (pdf.js).
  useEffect(() => {
    setFailed(false);
    setBlobUrl(null);
    if (!file || !isImage) return;

    let cancelled = false;
    let url: string | null = null;
    const controller = new AbortController();

    fetch(previewUrl(file.id), { credentials: "include", signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`preview HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch((err: unknown) => {
        if (!cancelled && (err as { name?: string }).name !== "AbortError") setFailed(true);
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [file?.id, isImage]);

  if (!file) {
    return null;
  }

  const dl = downloadUrl(file.id);

  let body: ReactNode;
  if (failed || !canPreview) {
    body = <PreviewError href={dl} />;
  } else if (isPdf) {
    body = (
      <Suspense fallback={<PreviewLoading />}>
        <PdfViewer fileId={file.id} onError={onPreviewError} />
      </Suspense>
    );
  } else if (!blobUrl) {
    body = <PreviewLoading />;
  } else {
    body = (
      <div className="flex max-h-[70vh] items-center justify-center overflow-auto bg-surface-muted p-4">
        <img
          src={blobUrl}
          alt={file.name}
          onError={() => setFailed(true)}
          className="max-h-[66vh] max-w-full object-contain"
        />
      </div>
    );
  }

  return (
    <Modal open={true} onClose={onClose} labelledBy={titleId} maxWidthClass="max-w-2xl">
      <div className="flex items-center gap-3 border-b border-surface-border px-5 py-3">
        <h2 id={titleId} className="min-w-0 flex-1 truncate text-body font-medium text-text">
          {file.name}
        </h2>
        <a
          href={dl}
          download
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-surface-border bg-surface px-3 text-body font-medium text-text hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <span aria-hidden="true">↓</span>
          <span>Download</span>
        </a>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <svg viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      </div>
      {body}
    </Modal>
  );
}
